// STEP LIVE-STUDIO-PC-PRODUCT-SELECT-UI-1 (2026-10-08) · 2026-10-08.
//
// 공통 '전체 상품에서 등록' modal.  SellerLiveControl.js 에 inline 로 존재하던
// AddProductModal 을 shared file 로 이동 (behavior 완전 동일 · 복사-신규 금지 지시 준수).
//
// REUSE:
//   · GET /seller/products
//   · POST /seller/broadcasts/{sid}/live-products (idempotent · 중복시 already_linked)
//   · 보기 크기 presets · currentIds 중복 가드 · 일괄 등록 로직 전부 그대로.
//
// Consumers:
//   · pages/SellerLiveControl.js (LIVE Console)
//   · pages/SellerLiveConsole.js (PC Studio)
import React, { useEffect, useMemo, useState } from 'react';
import { X, Search, Plus } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import SafeImg from '@/components/SafeImg';
import { useSellerProductSearch, mergeProducts } from '@/lib/useSellerProductSearch';

const won = (n) => `${(Number(n) || 0).toLocaleString('ko-KR')}원`;

const _PICKER_DENSITY_STORAGE_KEY = 'livemoa_live_product_picker_density';
const _PICKER_DENSITY_PRESETS = {
  default: {
    gridCols: 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5',
    innerPad: 'p-2',
    nameCls:  'text-[12px] font-bold truncate leading-tight',
    pidCls:   'text-[10px] text-gray-500 truncate leading-tight mt-0.5',
    priceCls: 'text-[12px] font-black tabular-nums leading-tight mt-0.5',
    btnTxt:   'text-[11px]',
    btnPy:    'py-1',
    badgeTxt: 'text-[9px]',
    showPid:  true,
    gap:      'gap-3',
  },
  half: {
    gridCols: 'grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8',
    innerPad: 'p-1.5',
    nameCls:  'text-[11px] font-bold truncate leading-tight',
    pidCls:   'text-[9px] text-gray-500 truncate leading-tight',
    priceCls: 'text-[11px] font-black tabular-nums leading-tight',
    btnTxt:   'text-[10px]',
    btnPy:    'py-0.5',
    badgeTxt: 'text-[8px]',
    showPid:  true,
    gap:      'gap-2',
  },
  quarter: {
    gridCols: 'grid-cols-4 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10 xl:grid-cols-12',
    innerPad: 'p-1',
    nameCls:  'text-[10px] font-bold truncate leading-tight',
    pidCls:   '',
    priceCls: 'text-[10px] font-black tabular-nums leading-tight',
    btnTxt:   'text-[9px]',
    btnPy:    'py-0.5',
    badgeTxt: 'text-[8px]',
    showPid:  false,
    gap:      'gap-1.5',
  },
};

const _hydratePickerDensity = () => {
  if (typeof window === 'undefined') return 'half';
  try {
    const v = window.localStorage.getItem(_PICKER_DENSITY_STORAGE_KEY);
    if (v && _PICKER_DENSITY_PRESETS[v]) return v;
  } catch { /* ignore */ }
  return 'half';
};

const AddProductModal = ({ open, onClose, currentIds, onAdded, scheduleId }) => {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selected, setSelected] = useState(() => new Set());
  const [busyPid, setBusyPid] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [density, setDensity] = useState(_hydratePickerDensity);
  const preset = _PICKER_DENSITY_PRESETS[density] || _PICKER_DENSITY_PRESETS.half;

  const changeDensity = (next) => {
    if (!_PICKER_DENSITY_PRESETS[next] || next === density) return;
    setDensity(next);
    try { window.localStorage.setItem(_PICKER_DENSITY_STORAGE_KEY, next); } catch { /* ignore */ }
  };
  useEffect(() => {
    if (!open) return;
    setQ('');
    setStatusFilter('all');
    setSelected(new Set());
    api.get('/seller/products').then((r) => setItems(Array.isArray(r.data) ? r.data : (r.data?.items || [])))
       .catch(() => setItems([]));
  }, [open, reloadKey]);

  // LIVE-SELLER-RECORDING-TEST-FLOW-1 · 기본 목록(500개) 밖 본인 상품도 검색어로 서버 조회.
  const found = useSellerProductSearch(q, open);
  const list = useMemo(() => {
    const term = q.trim().toLowerCase();
    return mergeProducts(items, found).filter((p) => {
      if (statusFilter !== 'all' && (p.sale_status || '') !== statusFilter) return false;
      if (!term) return true;
      const hay = [p.name, p.product_id, p.sku, p.brand]
                   .filter(Boolean).map(String).map((s) => s.toLowerCase());
      return hay.some((h) => h.includes(term));
    });
  }, [items, found, q, statusFilter]);

  const statusCounts = useMemo(() => {
    const base = { all: 0, '판매 중': 0, '품절': 0, '판매 종료': 0, '비공개': 0 };
    for (const p of items) {
      base.all += 1;
      const s = p.sale_status || '';
      if (base[s] !== undefined) base[s] += 1;
    }
    return base;
  }, [items]);

  const selectableInView = useMemo(() => (
    list.filter((p) => !currentIds.has(p.product_id)).map((p) => p.product_id)
  ), [list, currentIds]);

  const allInViewSelected = (
    selectableInView.length > 0
    && selectableInView.every((pid) => selected.has(pid)));

  const toggleOne = (pid) => setSelected((prev) => {
    const n = new Set(prev);
    if (n.has(pid)) n.delete(pid); else n.add(pid);
    return n;
  });

  const toggleSelectAll = () => setSelected((prev) => {
    const n = new Set(prev);
    if (allInViewSelected) {
      for (const pid of selectableInView) n.delete(pid);
    } else {
      for (const pid of selectableInView) n.add(pid);
    }
    return n;
  });

  const addOne = async (pid) => {
    if (busyPid || bulkBusy) return;
    setBusyPid(pid);
    try {
      const r = await api.post(`/seller/broadcasts/${scheduleId}/live-products`,
                               { product_id: pid });
      if (r.data?.already_linked) {
        toast.info('이미 오늘 방송에 등록된 상품입니다.');
      } else {
        toast.success('오늘 방송에 등록되었습니다.');
      }
      onAdded?.();
      setReloadKey((k) => k + 1);
      setSelected((prev) => { const n = new Set(prev); n.delete(pid); return n; });
    } catch (e) {
      toast.error(e?.response?.data?.detail || '등록 실패');
    } finally {
      setBusyPid(null);
    }
  };

  const bulkAdd = async () => {
    if (bulkBusy) return;
    const pids = Array.from(selected).filter((pid) => !currentIds.has(pid));
    if (pids.length === 0) {
      toast.error('등록할 상품이 없습니다.'); return;
    }
    setBulkBusy(true);
    let added = 0;
    let dup = 0;
    const failed = [];
    const addedSet = new Set();
    try {
      for (const pid of pids) {
        try {
          const r = await api.post(`/seller/broadcasts/${scheduleId}/live-products`,
                                   { product_id: pid });
          if (r.data?.already_linked) dup += 1;
          else { added += 1; addedSet.add(pid); }
        } catch (e) {
          const prod = items.find((p) => p.product_id === pid);
          failed.push({ pid, name: prod?.name || pid,
                        reason: e?.response?.data?.detail || '등록 실패' });
        }
      }
      setSelected((prev) => {
        const n = new Set(prev);
        for (const pid of addedSet) n.delete(pid);
        return n;
      });
      const parts = [];
      if (added > 0) parts.push(`${added}건 등록`);
      if (dup > 0) parts.push(`중복 ${dup}건 스킵`);
      if (failed.length > 0) parts.push(`실패 ${failed.length}건`);
      const msg = parts.join(' · ') || '처리 완료';
      if (failed.length > 0) toast.error(msg);
      else if (added > 0) toast.success(msg);
      else toast.info(msg);
      if (added > 0) onAdded?.();
      setReloadKey((k) => k + 1);
    } finally {
      setBulkBusy(false);
    }
  };

  if (!open) return null;

  const selectedCount = Array.from(selected).filter((pid) => !currentIds.has(pid)).length;

  const STATUS_TABS = [
    { key: 'all',       label: '전체' },
    { key: '판매 중',   label: '판매중' },
    { key: '품절',      label: '품절' },
    { key: '판매 종료', label: '판매종료' },
    { key: '비공개',    label: '비공개' },
  ];

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4"
         onClick={onClose}
         data-testid="add-product-modal">
      <div className="bg-white dark:bg-[#141414] rounded-t-2xl sm:rounded-2xl w-full max-w-4xl max-h-[90vh] sm:max-h-[85vh] flex flex-col"
           onClick={(e) => e.stopPropagation()}>
        <div className="px-5 pt-4 pb-3 border-b border-black/5 dark:border-white/10"
             data-testid="add-product-header">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-base font-black">전체 상품에서 등록</h3>
            <div className="flex items-center gap-2">
              <div className="inline-flex items-center gap-1"
                   data-testid="add-product-density-group">
                <span className="text-[10px] font-bold text-gray-500 mr-0.5 hidden sm:inline">보기 크기</span>
                {[
                  { k: 'default', label: '기본' },
                  { k: 'half',    label: '1/2' },
                  { k: 'quarter', label: '1/4' },
                ].map((d) => (
                  <button key={d.k}
                          type="button"
                          onClick={() => changeDensity(d.k)}
                          aria-pressed={density === d.k}
                          className={`px-2 py-1 rounded-full text-[11px] font-bold border transition-colors ${
                            density === d.k
                              ? 'bg-[#FF3B30] text-white border-[#FF3B30]'
                              : 'border-black/10 dark:border-white/10 text-gray-600 dark:text-gray-300 hover:border-[#FF3B30]/50'
                          }`}
                          data-testid={`add-product-density-${d.k}`}>
                    {d.label}
                  </button>
                ))}
              </div>
              <button onClick={onClose}
                      className="w-7 h-7 rounded-full hover:bg-black/5 dark:hover:bg-white/10 inline-flex items-center justify-center"
                      aria-label="닫기"
                      data-testid="add-product-close-top">
                <X size={16} />
              </button>
            </div>
          </div>
          <div className="relative mb-2">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)}
                   placeholder="상품명 또는 상품번호로 검색"
                   className="w-full pl-9 pr-3 py-2 border border-black/10 dark:border-white/10 rounded-full text-sm bg-black/[0.02] dark:bg-white/[0.03] focus:outline-none focus:border-[#FF3B30]"
                   data-testid="add-product-search" />
          </div>
          <div className="flex items-center flex-wrap gap-2 text-xs">
            <label className="inline-flex items-center gap-1.5 cursor-pointer select-none"
                   data-testid="add-product-select-all-label">
              <input type="checkbox"
                     checked={allInViewSelected}
                     onChange={toggleSelectAll}
                     disabled={selectableInView.length === 0}
                     className="h-3.5 w-3.5"
                     data-testid="add-product-select-all" />
              <span className="font-bold">전체 선택
                <span className="ml-1 text-gray-500 tabular-nums">({selectableInView.length})</span>
              </span>
            </label>
            <span className="w-px h-3 bg-black/10 dark:bg-white/15" />
            <div className="flex gap-1 flex-wrap" data-testid="add-product-status-filter">
              {STATUS_TABS.map((t) => (
                <button key={t.key}
                        onClick={() => setStatusFilter(t.key)}
                        className={`px-2.5 py-1 rounded-full text-[11px] font-bold border transition-colors ${
                          statusFilter === t.key
                            ? 'bg-[#FF3B30] text-white border-[#FF3B30]'
                            : 'border-black/10 dark:border-white/10 text-gray-600 dark:text-gray-300 hover:border-[#FF3B30]/50'
                        }`}
                        data-testid={`add-product-status-${t.key === 'all' ? 'all' : t.key.replace(/\s+/g, '-')}`}>
                  {t.label}<span className="ml-1 tabular-nums opacity-80">{statusCounts[t.key] || 0}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-3"
             data-testid="add-product-list-area"
             data-density={density}>
          {list.length === 0 ? (
            <p className="text-sm text-gray-400 py-10 text-center">
              {items.length === 0 ? '등록할 상품이 없습니다.' : '검색/필터 결과가 없습니다.'}
            </p>
          ) : (
            <ul className={`grid ${preset.gridCols} ${preset.gap} auto-rows-fr`}
                data-testid="add-product-grid"
                data-density={density}>
              {list.map((p) => {
                const dup = currentIds.has(p.product_id);
                const checked = selected.has(p.product_id);
                const ss = p.sale_status || '';
                const isSoldout = ss === '품절' || p.stock_status === 'out_of_stock';
                const isEnded = ss === '판매 종료';
                const soldLabel = isEnded ? '종료' : isSoldout ? '품절' : '판매 중';
                const soldTone = isEnded
                  ? 'bg-gray-500/70'
                  : isSoldout ? 'bg-amber-500/80' : 'bg-black/70';
                const indivBusy = busyPid === p.product_id;
                return (
                  <li key={p.product_id}
                      className={`relative rounded-xl border overflow-hidden flex flex-col h-full ${
                        dup
                          ? 'border-black/5 dark:border-white/5 opacity-70'
                          : checked
                            ? 'border-[#FF3B30] ring-2 ring-[#FF3B30]/30'
                            : 'border-black/10 dark:border-white/10 hover:border-[#FF3B30]/50'
                      }`}
                      data-testid={`add-product-card-${p.product_id}`}
                      data-dup={dup ? 'true' : 'false'}
                      data-selected={checked ? 'true' : 'false'}>
                    <label className="absolute top-1 left-1 z-10 bg-white/90 dark:bg-black/70 rounded-md p-0.5 cursor-pointer"
                           data-testid={`add-product-check-wrap-${p.product_id}`}>
                      <input type="checkbox"
                             checked={checked}
                             disabled={dup || bulkBusy || indivBusy}
                             onChange={() => toggleOne(p.product_id)}
                             className="h-3 w-3 cursor-pointer"
                             data-testid={`add-product-check-${p.product_id}`} />
                    </label>
                    <span className={`absolute top-1 right-1 z-10 px-1 py-0.5 rounded font-black text-white ${preset.badgeTxt} ${dup ? 'bg-[#FF3B30]' : soldTone}`}
                          data-testid={`add-product-status-badge-${p.product_id}`}>
                      {dup ? '등록됨' : soldLabel}
                    </span>
                    <div className="relative w-full bg-black/[0.03] dark:bg-white/[0.03] shrink-0"
                         style={{ paddingBottom: '100%' }}
                         data-testid={`add-product-image-box-${p.product_id}`}>
                      <SafeImg src={p.image} kind="product"
                               className="absolute inset-0 w-full h-full object-cover"
                               alt={p.name || ''} />
                    </div>
                    <div className={`${preset.innerPad} flex-1 flex flex-col min-h-0`}>
                      <p className={preset.nameCls}
                         data-testid={`add-product-name-${p.product_id}`}>{p.name}</p>
                      {preset.showPid && (
                        <p className={preset.pidCls}
                           data-testid={`add-product-pid-${p.product_id}`}>
                          {p.product_id?.slice(0, 10) || ''}
                        </p>
                      )}
                      <p className={preset.priceCls}
                         data-testid={`add-product-price-${p.product_id}`}>{won(p.sale_price)}</p>
                      <button type="button"
                              onClick={() => addOne(p.product_id)}
                              disabled={dup || bulkBusy || indivBusy}
                              className="mt-auto pt-1 w-full"
                              data-testid={`add-product-${p.product_id}`}>
                        <span className={`block ${preset.btnPy} rounded-full bg-black text-white ${preset.btnTxt} font-bold disabled:opacity-30`}>
                          <Plus size={10} className="inline mr-0.5" />
                          {dup ? '등록됨' : indivBusy ? '등록 중…' : '등록'}
                        </span>
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="px-5 py-3 border-t border-black/5 dark:border-white/10 flex items-center gap-2 flex-wrap"
             data-testid="add-product-action-bar">
          <span className="text-xs font-bold" data-testid="add-product-selected-count">
            <span className="tabular-nums">{selectedCount}</span>개 선택됨
          </span>
          <div className="flex-1" />
          <button onClick={onClose}
                  disabled={bulkBusy}
                  className="px-5 py-2 rounded-full border border-black/10 dark:border-white/10 text-xs font-bold disabled:opacity-40"
                  data-testid="add-product-cancel">닫기</button>
          <button onClick={bulkAdd}
                  disabled={selectedCount === 0 || bulkBusy}
                  className="px-5 py-2 rounded-full bg-[#FF3B30] text-white text-xs font-bold disabled:opacity-40 inline-flex items-center gap-1"
                  data-testid="add-product-bulk-register">
            <Plus size={12} />
            {bulkBusy ? '등록 중…' : '선택 상품 등록'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddProductModal;
export { AddProductModal, _PICKER_DENSITY_PRESETS, _PICKER_DENSITY_STORAGE_KEY };
